import { useCallback } from 'react'
import { eachDayOfInterval, format } from 'date-fns/esm'
import { useSelector } from 'react-redux'

import {
  resetBuildProgressConfig,
  setBuildProgressConfig,
  setMainSelection,
  setRouteOptimizationConflicts,
  useCloseModal,
  useEditingStateWithCtx,
  useRoutingSidebar,
} from '@/atoms'
import { useTexts as useRouteOptimizationTexts } from '@/components/modals/RouteOptimizationModal/useTexts'
import {
  buildRoutes as buildRoutesRequest,
  configToPayload,
  conflictVehicleSettings,
  strategyLabelFromConfig,
} from '@/features/domain/route'
import { selectCalendarRangeInterval } from '@/features/domain/ui'
import { getMapInfo, setPendingFitMap } from '@/map'
import { useNotification } from '@/hooks'
import { useAppDispatch } from '@/store'
import { useNavigate } from '@/routing'

import { useTexts } from '../useTexts'

export const useActions = (
  showBuildProgressModal: () => void,
  showCopyOrdersModal?: () => void,
  showRouteOptimizationModal?: () => void,
) => {
  const texts = useTexts()
  const routeOptimizationTexts = useRouteOptimizationTexts()
  const toast = useNotification()
  const dispatch = useAppDispatch()
  const navigate = useNavigate(true)
  const { setEditing } = useEditingStateWithCtx('order')
  const [, setRoutingSidebar] = useRoutingSidebar()
  const { start, end } = useSelector(selectCalendarRangeInterval)
  const closeModal = useCloseModal()

  const addOrder = useCallback(() => {
    setMainSelection('orderSteps', 'reset')
    setPendingFitMap([getMapInfo().center])
    navigate('orders')
    setEditing([])
  }, [setEditing, navigate])

  // Open the Build Progress modal and dispatch the build. On failure the modal
  // can't recover on its own (no close button while in progress), so close it,
  // clear the published config, and surface the backend error.
  const runBuild = useCallback(
    (payload?: uui.domain.actions.rpc.route.BuildRoutesPayload) => {
      showBuildProgressModal()
      dispatch(buildRoutesRequest(payload)).then(result => {
        if (!buildRoutesRequest.fulfilled.match(result)) {
          resetBuildProgressConfig()
          closeModal()
          toast.error(result.payload?.message ?? texts.buildRoutesError)
        }
      })
    },
    [dispatch, showBuildProgressModal, closeModal, toast, texts],
  )

  // Instant Build always runs the standard optimization (Fleet savings + Minimize
  // drivers). Balanced Routes is only reachable via Custom Build. The backend
  // decides the optimizer version.
  const buildRoutes = useCallback(() => {
    const config: uui.domain.server.rm.CustomBuildConfiguration = {
      strategy: null,
      minimizeVehicles: true,
      lastUpdateDate: '',
    }

    setBuildProgressConfig({
      strategyLabel: strategyLabelFromConfig(config, routeOptimizationTexts),
    })
    runBuild(configToPayload(config, false))
  }, [runBuild, routeOptimizationTexts])

  const exportToOperations = useCallback(async () => {
    showCopyOrdersModal?.()
  }, [showCopyOrdersModal])

  const showDispatchPanel = useCallback(() => {
    setRoutingSidebar({ view: 'dispatchPanel' })
  }, [setRoutingSidebar])

  // Prefetch the constraint conflicts, then open the Route Optimization modal so
  // it renders complete (alert in place) in one paint — same prefetch approach as
  // the Instant Build conflict modal. On a failed pre-flight we open with no
  // conflicts rather than block the user.
  const customBuild = useCallback(async () => {
    try {
      const buildDates = eachDayOfInterval({ start, end }).map(d => format(d, 'yyyyMMdd'))
      const result = await dispatch(conflictVehicleSettings({ buildDates }))
      setRouteOptimizationConflicts(
        conflictVehicleSettings.fulfilled.match(result) ? result.payload : null,
      )
    } catch {
      setRouteOptimizationConflicts(null)
    }
    showRouteOptimizationModal?.()
  }, [dispatch, start, end, showRouteOptimizationModal])

  return { addOrder, buildRoutes, customBuild, showDispatchPanel, exportToOperations }
}
